import { AnyObject } from '@triare/auth-redux';
import React, { useEffect } from 'react';
import { ExclamationCircleFilled, ReloadOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { ButtonProps } from 'antd/es/button/button';

import { useSimpleModal } from '../../Common/Modal/Simple';
import { useAuth } from '../../../store/auth';
import { useMessageError, useMessageSuccess } from '../../../hooks/common';
import { listAction } from '../../Common/Table';
import {
  BusinessPartnerTableRow, useBusinessDelete, useBusinessSyncWithMBS,
} from '../../../hooks/api/businessPartners';

interface ActionButtonProps<RequestRes = undefined> {
  record: BusinessPartnerTableRow; // Partial<UserTableRow>
  buttonProps?: ButtonProps;
  setTableKey?: (newVal: number) => void;
  afterSuccess?: (res?: RequestRes) => void;
}

export function DeleteButton({
  record, setTableKey, afterSuccess, buttonProps,
}: ActionButtonProps & AnyObject) {
  const businessDelete = useBusinessDelete();
  const { open, contextHolder } = useSimpleModal();
  const { user } = useAuth();

  useMessageError([businessDelete]);
  useMessageSuccess([businessDelete], 'Business partner deleted successfully');

  useEffect(() => {
    if (!businessDelete.error && !businessDelete.loading && businessDelete.data) {
      setTableKey?.(Date.now());
      afterSuccess?.();
    }
  }, [businessDelete.error, businessDelete.loading, businessDelete.data]);

  const ActionButton = listAction.delete;

  if (user?.id === record.id) {
    return null;
  }

  return (
    <>
      <Tooltip title="Delete business partner" placement="topRight">
        <ActionButton
          loading={businessDelete.loading}
          title="Delete business partner"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            open({
              icon: <ExclamationCircleFilled />,
              title: 'Delete business partner?',
              content: (
                <span>
                  Are you sure you want to delete business partner
                  {' '}
                  <b>{record.fullName || '-'}</b>
                  ?
                </span>
              ),
              cancelText: 'Cancel',
              okText: 'Delete',
              okButtonProps: {
                danger: true,
              },
              onOk: () => businessDelete.fetch(record.id),
            });
          }}
          {...buttonProps}
        />
      </Tooltip>
      {contextHolder}
    </>
  );
}

export function SyncBusinessButton({
  record, setTableKey, afterSuccess, buttonProps,
}: ActionButtonProps & AnyObject) {
  const businessSyncWithMBS = useBusinessSyncWithMBS();
  const { open, contextHolder } = useSimpleModal();
  const { user } = useAuth();

  useMessageError([businessSyncWithMBS]);
  useMessageSuccess([businessSyncWithMBS], 'Business synchronized successfully');

  useEffect(() => {
    if (!businessSyncWithMBS.error && !businessSyncWithMBS.loading && businessSyncWithMBS.data) {
      setTableKey?.(Date.now());
      afterSuccess?.();
    }
  }, [businessSyncWithMBS.error, businessSyncWithMBS.loading, businessSyncWithMBS.data]);

  if (user?.id === record.id) {
    return null;
  }

  return (
    <>
      <Tooltip title="Sync business partner">
        <Button
          key="changeStatus"
          type="text"
          icon={<ReloadOutlined />}
          loading={businessSyncWithMBS.loading}
          title="Sync business partner"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            open({
              icon: <ExclamationCircleFilled />,
              title: 'Sync business partner?',
              content: (
                <span>
                  Are you sure you want to sync business partner
                  {' '}
                  <b>{record.fullName || '-'}</b>
                  ?
                </span>
              ),
              cancelText: 'Cancel',
              okText: 'Sync',
              onOk: () => businessSyncWithMBS.fetch(undefined, `${record.businessPartnerNumber}/parse`),
            });
          }}
          {...buttonProps}
        />
      </Tooltip>
      {contextHolder}
    </>
  );
}
